import {
  AtlasConfiguration,
  LandingPageAudience,
  LandingPageItem,
} from '../models/commune';

/**
 * Whether the commune configures separate landing pages for the public and
 * the internal atlas. Until it does, both share `landingPageItems`.
 *
 * @param {AtlasConfiguration | undefined} config - The commune's atlas configuration.
 * @return {boolean} True if the landing pages are separated.
 */
export function hasSeparateLandingPages(
  config: AtlasConfiguration | undefined
): boolean {
  return !!config?.separateLandingPages;
}

/**
 * Resolves the landing page items shown to the given audience.
 *
 * @param {AtlasConfiguration | undefined} config - The commune's atlas configuration.
 * @param {LandingPageAudience} audience - Public or internal atlas.
 * @return {LandingPageItem[]} The items of that landing page.
 */
export function getLandingPageItems(
  config: AtlasConfiguration | undefined,
  audience: LandingPageAudience
): LandingPageItem[] {
  if (audience === 'public' && hasSeparateLandingPages(config)) {
    return config?.landingPageItemsPublic || [];
  }
  return config?.landingPageItems || [];
}

/**
 * Returns a copy of the configuration with the items of the given audience's
 * landing page replaced (the shared list while they are not separated).
 *
 * @param {AtlasConfiguration} config - The commune's atlas configuration.
 * @param {LandingPageAudience} audience - Public or internal atlas.
 * @param {LandingPageItem[]} items - The new items.
 * @return {AtlasConfiguration} The updated configuration copy.
 */
export function withLandingPageItems(
  config: AtlasConfiguration,
  audience: LandingPageAudience,
  items: LandingPageItem[]
): AtlasConfiguration {
  if (audience === 'public' && hasSeparateLandingPages(config)) {
    return { ...config, landingPageItemsPublic: items };
  }
  return { ...config, landingPageItems: items };
}
